import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CalendarPlus, CheckCheck, Clock, Loader2, Moon, PartyPopper, Send, UserRound } from 'lucide-react';
import moment from 'moment';
import { namespace } from '@common-config/constants';
import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { DBUserMessageThunks } from '@common/reducers/DBServiceThunks';
import { scheduleMessageTool } from '@common/thunks/UserMessagesThunks';
import { ensureClientIdempotencyKey } from '@common/utils/client-idempotency-key';
import { MessageState } from '@common/types/interface';
import { ConfettiBurst } from '@common/components/confetti-burst/confetti-burst';
// The SAME recipient picker as the schedule-message dialog (contacts, groups, channels, or a typed number).
import { ContactSelect } from '@extension/context/content/components/add-scheduled-message-button/send-message-to/contact-select';
import type { ContactPickItem } from '@extension/context/content/components/add-scheduled-message-button/schedule-message-types';
import { firstMessageDueDate, formatClock, trackedStatus, TrackedStatus } from './onboarding-logic';
import { C, Heading, Notice, OptionRow, PrimaryButton } from './onboarding-ui';
import { GuideVideoButton } from '@/common/components/guide-video/guide-video-button';

const TRACK_KEY = 'bt-onboarding-first-message-v1';

interface Tracked { id: string; dueAt: number; text: string; to: string; self: boolean; delivered?: boolean; at?: number; toName?: string }

const readTracked = (): Tracked | null => {
  try {
    const raw = window.sessionStorage.getItem(TRACK_KEY);
    return raw ? JSON.parse(raw) as Tracked : null;
  } catch { return null; }
};
const writeTracked = (v: Tracked | null) => {
  try {
    if (v) window.sessionStorage.setItem(TRACK_KEY, JSON.stringify(v));
    else window.sessionStorage.removeItem(TRACK_KEY);
  } catch { /* storage blocked — tracking just won't survive a reload */ }
};


/** A WhatsApp chat in miniature: the "You" chat, with the user's own outgoing bubble. */
const ChatPreview: React.FC<{
  toLabel: string;
  toSub: string;
  children: React.ReactNode;
  meta: React.ReactNode;
  bubbleKey?: string;
  /** The bubble is the text field — show it on hover/focus. */
  editable?: boolean;
  /** Scheduled, not sent: a faded, dashed draft — it only turns solid once it really went out. */
  pending?: boolean;
}> = ({ toLabel, toSub, children, meta, bubbleKey, editable, pending }) => (
  <div className="onb-chat" style={{ borderRadius: 20, overflow: 'hidden', border: `1px solid ${C.border}`, background: 'var(--chat)' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--head)', color: '#fff' }}>
      <span aria-hidden style={{ width: 36, height: 36, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,.22)' }}><UserRound size={19} /></span>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 650 }}>{toLabel}</span>
        <span dir="ltr" style={{ fontSize: 12.5, opacity: 0.8, textAlign: 'start' }}>{toSub}</span>
      </span>
    </div>
    <div style={{ padding: '26px 16px 22px', minHeight: 132, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div key={bubbleKey} className={editable ? 'onb-bubble-in onb-editable' : 'onb-bubble-in'} style={{ alignSelf: 'flex-end', width: 'min(100%, 380px)', padding: '9px 11px 6px', borderRadius: '12px 12px 3px 12px', background: pending ? 'color-mix(in srgb, var(--out) 45%, transparent)' : 'var(--out)', color: 'var(--out-text)', boxShadow: pending ? 'none' : '0 1px .5px rgba(11,20,26,.13)', outline: pending ? '1.5px dashed color-mix(in srgb, var(--meta) 70%, transparent)' : 'none', outlineOffset: -1.5, opacity: pending ? 0.85 : 1, transition: 'background .4s ease, opacity .4s ease' }}>
        <div style={{ fontSize: 15, lineHeight: 1.45 }}>{children}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 11.5, color: 'var(--meta)' }}>{meta}</div>
      </div>
    </div>
  </div>
);

export const FirstMessageStep: React.FC<{
  ownWid: string | null;
  /** Still asking the server for the user's own number (just after connecting). */
  findingNumber?: boolean;
  kind: 'local' | 'remote';
  justConnected: boolean;
  alreadyDone: boolean;
  isPro: boolean;
  proPrice: string | null;
  startedAt: number | null;
  onScheduleReal: () => void;
  onAlwaysOn: () => void;
  onTrack: (name: string, props?: Record<string, unknown>) => void;
  /** The tracked message went out — the page marks the whole setup done. */
  onDelivered: () => void;
}> = ({ ownWid, findingNumber, kind, justConnected, alreadyDone, isPro, proPrice, startedAt, onScheduleReal, onAlwaysOn, onTrack, onDelivered }) => {
  const { t } = useTranslation(namespace);
  const o = (key: string, opts?: Record<string, unknown>) => t(`onboarding.${key}`, opts);
  const dispatch = useAppDispatch();

  const [text, setText] = useState(() => o('message.default'));
  // Recipient: the user's own number by default (proof the connection is theirs); any contact,
  // group or typed number from the standard picker instead. 'none' = they cleared it.
  const [picked, setPicked] = useState<ContactPickItem | 'none' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tracked, setTracked] = useState<Tracked | null>(readTracked);
  const [status, setStatus] = useState<TrackedStatus>(() => (readTracked()?.delivered ? 'sent' : 'scheduled'));
  // "Late" only means something once we've actually read the stored status.
  const [polled, setPolled] = useState(false);
  // A delivery already celebrated (on an earlier load) is shown, never re-announced.
  const announced = useRef(!!readTracked()?.delivered);
  const [now, setNow] = useState(Date.now());

  const ownDigits = ownWid ? ownWid.replace(/@.*/, '') : '';
  const selfContact: ContactPickItem | null = ownWid
    ? { id: ownWid, name: o('message.toYou'), displayName: `${o('message.toYou')} · +${ownDigits}` }
    : null;
  const recipient: ContactPickItem | null = picked === 'none' ? null : picked ?? selfContact;
  const to = recipient?.id ?? null;
  const toIsSelf = !!to && to === ownWid;
  const toName = toIsSelf ? o('message.toYou') : (recipient?.displayName || recipient?.name || o('message.toOther'));

  // Textarea grows with the text, like the WhatsApp compose box.
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text, tracked]);

  // Follow the stored message until it is out: the scheduler (and the engine) write its status.
  useEffect(() => {
    if (!tracked || status !== 'scheduled') return undefined;
    let alive = true;
    const poll = async () => {
      try {
        const res: any = await dispatch(DBUserMessageThunks.find({ _id: tracked.id } as any)).unwrap();
        const row = Array.isArray(res) ? res[0] : res?.data?.[0];
        if (alive) setPolled(true);
        if (alive && row) setStatus(trackedStatus(row.status));
      } catch { /* transient — try again on the next tick */ }
    };
    poll();
    const id = window.setInterval(poll, 3000);
    return () => { alive = false; window.clearInterval(id); };
  }, [tracked, status, dispatch]);

  // Countdown ticks every second while waiting; on the compose screen the "Sends at" minute
  // only needs to stay current.
  useEffect(() => {
    if (tracked && status !== 'scheduled') return undefined;
    const id = window.setInterval(() => setNow(Date.now()), tracked ? 1000 : 10_000);
    return () => window.clearInterval(id);
  }, [tracked, status]);

  const late = !!tracked && polled && status === 'scheduled' && now - tracked.dueAt > 120_000;
  useEffect(() => { if (late) onTrack('onboarding_stuck', { step: 'first_message' }); }, [late]); // eslint-disable-line react-hooks/exhaustive-deps

  const delivered = status === 'sent';
  useEffect(() => {
    if (!delivered) return;
    onDelivered();
    if (announced.current) return;
    announced.current = true;
    if (tracked) writeTracked({ ...tracked, delivered: true });
    onTrack('onboarding_first_message_delivered', { seconds_after_due: tracked ? Math.round((Date.now() - tracked.dueAt) / 1000) : null });
  }, [delivered]); // eslint-disable-line react-hooks/exhaustive-deps

  // Confetti from the bubble the moment it goes out.
  const doneRef = useRef<HTMLSpanElement | null>(null);
  const [burst, setBurst] = useState<{ x: number; y: number } | null>(null);
  const [celebrate] = useState(() => !readTracked()?.delivered);
  useEffect(() => {
    if (!delivered || !doneRef.current || !celebrate) return;
    const r = doneRef.current.getBoundingClientRect();
    setBurst({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
  }, [delivered]);

  // Confetti from the "connected" chip the moment WhatsApp connects here.
  const chipRef = useRef<HTMLDivElement | null>(null);
  const [connectBurst, setConnectBurst] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => {
    if (!justConnected || !chipRef.current) return;
    const r = chipRef.current.getBoundingClientRect();
    setConnectBurst({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
  }, [justConnected]);

  const schedule = async () => {
    if (!to || !text.trim() || busy) return;
    setBusy(true);
    setError(null);
    const due = firstMessageDueDate(new Date());
    try {
      const stored: any = await dispatch(scheduleMessageTool({
        message: text.trim(),
        contactList: [{ id: to, name: '' }],
        dueDate: due,
        isRecurring: false,
        cancelIfReceived: false,
        state: MessageState.pending,
        clientIdempotencyKey: ensureClientIdempotencyKey(null),
      } as any)).unwrap();
      const id = (Array.isArray(stored) ? stored[0] : stored)?._id;
      if (!id) throw new Error('no id');
      const next = { id, dueAt: due.getTime(), text: text.trim(), to, self: toIsSelf, at: Date.now(), toName };
      writeTracked(next);
      setStatus('scheduled');
      setPolled(false);
      setTracked(next);
      onTrack('onboarding_first_message_scheduled', { to_self: toIsSelf });
    } catch (e: any) {
      setError(e?.data?.message || e?.message || o('message.errorGeneric'));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { writeTracked(null); setTracked(null); setStatus('scheduled'); setPolled(false); };

  // A person shows their number under the name; a group / channel just its name.
  const subFor = (wid: string | null | undefined) => (wid && wid.endsWith('@c.us') ? `+${wid.replace('@c.us', '')}` : '');
  const nameFor = (tr: Tracked) => (tr.self ? o('message.toYou') : tr.toName || o('message.toOther'));

  /* ── Done: the first message is out (now, or on an earlier visit) ── */
  if (delivered || (alreadyDone && !tracked)) {
    const elapsed = startedAt ? Date.now() - startedAt : null;
    return (
      <div className="onb-in" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {burst && <ConfettiBurst origin={burst} />}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span ref={doneRef} className="onb-pop" aria-hidden style={{ width: 56, height: 56, borderRadius: 999, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'color-mix(in srgb, #16a34a 14%, transparent)', color: C.success }}>
            <PartyPopper size={28} />
          </span>
          <Heading
            title={delivered ? o('done.titleSent') : o('done.title')}
            body={delivered
              ? (tracked?.self ? o('done.bodySelf') : o('done.bodyOther'))
              : o('done.bodyReturning')}
          />
        </div>
        {tracked && (
          <ChatPreview toLabel={nameFor(tracked)} toSub={subFor(tracked.to)} meta={<span className="onb-ticks" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{o('message.sent')}<CheckCheck size={16} strokeWidth={2.5} style={{ color: '#53bdeb' }} /></span>}>
            <span style={{ whiteSpace: 'pre-wrap' }}>{tracked.text}</span>
          </ChatPreview>
        )}
        {elapsed !== null && delivered && elapsed < 5 * 60_000 && (
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.muted }}>{o('done.elapsed', { time: formatClock(elapsed) })}</p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <PrimaryButton icon={<CalendarPlus size={18} />} onClick={onScheduleReal}>{o('done.scheduleReal')}</PrimaryButton>
          <GuideVideoButton lesson="message-planner" variant="link" surface="onboarding_done" />
        </div>
        {kind === 'local' && (
          <OptionRow
            icon={<Moon size={18} />}
            title={o('done.upsellTitle')}
            body={o('done.upsellBody')}
            cta={isPro ? o('done.upsellCtaPro') : proPrice ? o('done.upsellCtaPrice', { price: proPrice }) : o('done.upsellCta')}
            badge={o('choose.pro')}
            onClick={onAlwaysOn}
          />
        )}
      </div>
    );
  }

  /* ── Scheduled: watch it go out ── */
  if (tracked) {
    const failed = status === 'failed';
    const left = tracked.dueAt - now;
    const at = moment(tracked.dueAt).format('LT');
    const span = Math.max(1, tracked.dueAt - (tracked.at ?? tracked.dueAt - 60_000));
    const progress = Math.min(1, Math.max(0, 1 - left / span));
    return (
      <div className="onb-in" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <Heading
          title={failed ? o('message.failedTitle') : o('message.scheduledTitle', { time: at })}
          body={failed ? o('message.failedBody') : o(kind === 'local' ? 'message.scheduledBodyLocal' : 'message.scheduledBody', { time: at })}
        />
        {!failed && (
          // The one thing to read here: when it goes out, and how long until it does.
          <div role="timer" aria-live="off" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderRadius: 16, background: C.surface, border: `1px solid ${C.border}` }}>
            <span aria-hidden style={{ position: 'relative', width: 44, height: 44, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.ink }}>
              <svg width="44" height="44" viewBox="0 0 44 44" style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}>
                <circle cx="22" cy="22" r="19" fill="none" stroke="var(--onb-ink-soft)" strokeWidth="4" />
                <circle cx="22" cy="22" r="19" fill="none" stroke="var(--onb-ink)" strokeWidth="4" strokeLinecap="round" pathLength={100} strokeDasharray={`${progress * 100} 100`} style={{ transition: 'stroke-dasharray 1s linear' }} />
              </svg>
              <Clock size={18} strokeWidth={2.4} />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 15.5, fontWeight: 700, color: C.text }}>{o('message.sendsToday', { time: at })}</span>
              <span style={{ display: 'block', fontSize: 13.5, color: C.muted, marginTop: 2 }}>{o('message.notSentYet')}</span>
            </span>
            <span style={{ textAlign: 'end' }}>
              <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.muted }}>{left > 0 ? o('message.countdownLabel') : ''}</span>
              <span style={{ display: 'block', fontSize: 26, fontWeight: 750, color: C.inkText, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{left > 0 ? formatClock(left) : o('message.sendingNow')}</span>
            </span>
          </div>
        )}
        <ChatPreview
          toLabel={nameFor(tracked)}
          toSub={subFor(tracked.to)}
          bubbleKey="scheduled"
          pending={!failed}
          meta={failed
            ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#dc2626' }}><AlertTriangle size={13} />{o('message.notSent')}</span>
            : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Clock size={12} strokeWidth={2.5} />{o('message.scheduledMeta', { time: at })}</span>}
        >
          <span style={{ whiteSpace: 'pre-wrap' }}>{tracked.text}</span>
        </ChatPreview>
        {late && !failed && (
          <Notice icon={<AlertTriangle size={16} />} action={<a className="onb-link" href="/connections">{o('message.checkConnection')}</a>}>
            {o(kind === 'local' ? 'message.lateLocal' : 'message.lateRemote')}
          </Notice>
        )}
        {failed && <div><PrimaryButton icon={<Send size={17} />} onClick={reset}>{o('message.tryAgain')}</PrimaryButton></div>}
      </div>
    );
  }

  /* ── Compose: one bubble, one button ── */
  return (
    <div className="onb-in" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {connectBurst && <ConfettiBurst origin={connectBurst} />}
      {justConnected && (
        <div ref={chipRef} className="onb-pop" role="status" style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 14px', borderRadius: 999, background: C.brand, color: C.brandText, fontSize: 14, fontWeight: 700, boxShadow: '0 8px 18px -8px var(--onb-glow)' }}>
          <CheckCheck size={16} strokeWidth={2.5} />{o(kind === 'remote' ? 'message.connectedRemote' : 'message.connectedLocal')}
        </div>
      )}
      <Heading title={o('message.title')} body={o('message.body')} />
      <ChatPreview
        toLabel={toName}
        toSub={subFor(to)}
        editable
        meta={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Clock size={12} strokeWidth={2.5} />{o('message.sendsAt', { time: moment(firstMessageDueDate(new Date(now))).format('LT') })}</span>}
      >
        <textarea
          ref={areaRef}
          className="onb-textarea"
          rows={1}
          value={text}
          maxLength={500}
          aria-label={o('message.textLabel')}
          onChange={(e) => setText(e.target.value)}
        />
      </ChatPreview>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 650, color: C.text }}>{o('message.sendTo')}</span>
        {!ownWid && findingNumber && picked === null && (
          <span role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: C.muted }}>
            <Loader2 size={14} className="animate-spin" aria-hidden />{o('message.findingNumber')}
          </span>
        )}
        <ContactSelect
          selectedContactIds={recipient ? [recipient.id] : []}
          selectedContacts={recipient ? [recipient] : []}
          // One recipient for the first message: picking someone replaces the current one.
          onContactSelect={(c) => setPicked(c)}
          onContactRemove={() => setPicked('none')}
        />
        {toIsSelf && <span style={{ fontSize: 13.5, color: C.muted }}>{o('message.toSelfHint')}</span>}
      </div>
      {error && <Notice icon={<AlertTriangle size={16} />}>{error}</Notice>}
      <div><PrimaryButton icon={<Send size={18} />} busy={busy} disabled={!to || !text.trim()} onClick={schedule}>{o('message.cta')}</PrimaryButton></div>
    </div>
  );
};
